import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import InfiniteScroll from "react-swr-infinite-scroll";

import truncate from "lodash/truncate";
import { twMerge } from "tailwind-merge";

import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import AnnoucementSnippet from "components/AnnouncementSnippet";
import { Loader } from "components/customElements/loader";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import SignUpForm from "components/SignUpForm";
import useActivityTracker from "hooks/useActivityTracker";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useEffectOnce } from "hooks/useEffectOnce";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import shortSecurity from "utils/shortSecurity";

import styles from "./style.module.scss";

const EarningsAlertModal = dynamic(() => import("./EarningsAlertModal"));

type EntityPageEarningsAlertListProps = {};

export const entityEarningsAlertUrl = (
  slug: string,
  pageIndex: number,
  pageSize?: number,
  filterTxt?: string
) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/earnings-alerts`);
  3;
  url.searchParams.append("page[size]", pageSize ? pageSize.toString() : "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "-created-at");
  url.searchParams.append("include", "entity");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  return url.href;
};

const EntityPageEarningsAlertList = ({}: EntityPageEarningsAlertListProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const itemId = router.query.item as string;
  const { authenticated } = useAuthentication();

  const { trackActivity } = useActivityTracker();

  const { data: entity } = useApi(entityUrl(slug));

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  const { data, mutate, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;
      return authenticated
        ? entityEarningsAlertUrl(slug, pageIndex + 1, 5, filterTxt)
        : null;
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );

  const hasMore = data?.[size - 1]?.meta?.totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;

  useEffectOnce(
    (ack) => {
      if (entity?.id) {
        ack();
        trackActivity("earnings.list", entity);
      }
    },
    [entity?.id]
  );

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstFiling = data?.[0]?.data?.[0];
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Announcements - Earnings`;
  const description = truncate(
    `Latest Earnings on ${name} (${entity?.uniqueTicker})${
      firstFiling ? ` - ${firstFiling.title}` : ""
    }`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  if (!authenticated) {
    return <SignUpForm type="insight-signup" />;
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <div className={styles.container}>
        <div
          className={twMerge(
            styles.filter,
            "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center"
          )}
        >
          <span
            className={twMerge(styles.refresh, "hidden md:block")}
            onClick={() => mutate()}
          >
            Refresh
          </span>
          <div
            className={twMerge(
              styles.filterBox,
              "ml-0 md:ml-[30px] py-[5px] mr-[10px] w-full"
            )}
          >
            <FontAwesomeIcon icon={faSearch} />
            <OverlayTrigger
              placement="bottom"
              overlay={
                filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
              }
            >
              <input
                value={filterTxtValue}
                autoComplete="false"
                autoCapitalize="false"
                onChange={(e) => setFilterTxtValue(e.target.value)}
                aria-label="search"
                placeholder="Search"
                className="w-full md:max-w-[240px]"
              />
            </OverlayTrigger>
          </div>
        </div>
        <div className={styles.list}>
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader numberOfItems={5} />
              ) : isEmpty ? (
                <EmptyState
                  type={filterTxt ? "search" : "earnings-alert"}
                  query={filterTxt}
                  onClearClick={() => setFilterTxtValue("")}
                  entity={entity}
                />
              ) : (
                <div className={styles.endOfList}>No more earnings</div>
              )
            }
          >
            {(pageData: any) => {
              if (!pageData) return null;
              return pageData?.data?.map?.((item: any) => {
                const data = {
                  ...item,
                  detail: `
                    ${item.bodyListicle
                      .replace(/```html/g, "")
                      .replace(/```/g, "")}
                  `,
                };

                return (
                  <AnnoucementSnippet key={item.id} item={data} useModal />
                );
              });
            }}
          </InfiniteScroll>
        </div>
      </div>
      {itemId && <EarningsAlertModal id={itemId} />}
    </>
  );
};

export default EntityPageEarningsAlertList;
